// Casino book resolver — ported from tenexch frontend/src/utils/casinoBook.js
// plus the per-game special cases of tenexch hooks/casino/useCasinoMyBets.js.
//
// The book of every selection is CALCULATED from the user's open bets on the
// round (instead of the get-exposure API). User perspective:
//   BACK S (odds o, stake st) → book[S] += (o-1)*st, rest of that market -st
//   LAY  S (odds o, stake st) → book[S] -= (o-1)*st, rest of that market +st
// Single-line (fancy) markets show the liability on the backed line.

// Game types whose tenexch page passes GAME_TYPE + game object to
// useCasinoMyBets (computed book). Every other table uses the legacy
// get-exposure endpoint. d99 gameId === tenexch GAME_TYPE.
export const COMPUTED_BOOK_GAMES = new Set([
  "lucky7eu2", "teen33", "teen41", "aaa", "cmeter1", "race20", "teensin",
  "poker20", "goal2", "war", "teen1", "btable", "card32", "teen9", "dum10",
  "teen", "teen120", "notenum", "race2", "ab20", "card32eu", "3cardj",
  "lucky7", "teenmuf", "race17", "trio", "teen8", "dtl20", "lucky15",
  "poker6", "trap", "queen", "teen6", "teen20b", "baccarat", "cmeter", "dt20",
  "baccarat2", "abj", "dt6", "teen20", "teen42", "teen32", "patti2", "ab3", "mogambo",
  "worli", "worli2", "dt202", "cmatch20", "sicbo2", "teen3", "lucky7eu",
  "aaa2", "poker",
  // Goal: two markets (next scorer / method of goal), see computeCasinoBook.
  "goal",
  // SuperOver family (book game object built with superOverBookGameObj)
  "cricketv3", "superover", "superover2", "superover3",
]);

export const usesComputedBook = (gameType) => COMPUTED_BOOK_GAMES.has(gameType);

// markets = [{ sid, nat }] groups. Book is keyed by `sid:<sid>` (so the same
// name in two markets doesn't leak) and by the plain nat when it is unique.
export function computeUserBook(bets = [], markets = [], opts = {}) {
  // singleLineWin: the backed side-bet cell shows the POTENTIAL PROFIT
  // (+(odds-1)*stake) instead of the liability (teen9 / teen8).
  const singleLineWin = Boolean(opts.singleLineWin);
  const bySid = {};
  markets.forEach((sels) => sels.forEach(({ sid }) => {
    bySid[`sid:${sid}`] = 0;
  }));

  bets.forEach((bet) => {
    const sel = bet.selection || bet.player_name || bet.nation || bet.nat || "";
    const odds = parseFloat(bet.odds ?? bet.rate) || 0;
    const stake = parseFloat(bet.stake ?? bet.amount) || 0;
    const isLay = String(bet.type || bet.betType || "").toLowerCase() === "lay";
    if (!sel || !odds || !stake) return;

    // A bet carries only the name — first market containing it wins.
    let market = markets.find((m) => m.some((x) => x.nat === sel));
    if (!market) {
      // Session (line) markets store the LINE with the selection — "Total A 15",
      // "Ind Over 1 15" — because settlement compares the result against it. The
      // feed's runner is the bare name, so fall back to that; only reached when
      // the exact name matched nothing, so it cannot hijack a real runner whose
      // own name ends in a number.
      const base = sel.replace(/\s+\d+(?:\.\d+)?$/, "");
      if (base !== sel) market = markets.find((m) => m.some((x) => x.nat === base));
    }
    if (!market) return;

    if (market.length === 1) {
      const { sid } = market[0];
      if (singleLineWin) {
        bySid[`sid:${sid}`] += (isLay ? -1 : 1) * (odds - 1) * stake;
      } else {
        bySid[`sid:${sid}`] += isLay ? -(odds - 1) * stake : -stake;
      }
      return;
    }

    market.forEach(({ sid, nat }) => {
      const delta = nat === sel
        ? (isLay ? -1 : 1) * (odds - 1) * stake
        : (isLay ? 1 : -1) * stake;
      bySid[`sid:${sid}`] += delta;
    });
  });

  const natCount = {};
  markets.forEach((sels) => sels.forEach(({ nat }) => {
    natCount[nat] = (natCount[nat] || 0) + 1;
  }));
  const book = { ...bySid };
  markets.forEach((sels) => sels.forEach(({ sid, nat }) => {
    if (natCount[nat] === 1) book[nat] = bySid[`sid:${sid}`];
  }));

  return book;
}

// SuperOver keeps its runners under t2[].section; flatten into the
// { sub:[{sid,nat,etype,subtype}] } shape getMarketsForGame expects.
export function superOverBookGameObj(t2 = []) {
  return {
    sub: (t2 || []).flatMap((m) => {
      const isMatch = m.dtype === 2 || m.gtype === "match1";
      return (m.section || []).map((s) => ({
        sid: s.sid,
        nat: s.nat,
        etype: isMatch ? "match" : "fancy",
        subtype: isMatch ? "so_bookmaker" : `so_${s.sid}`,
      }));
    }),
  };
}

// etype match → same subtype = ONE market (book spreads); etype fancy* →
// every selection its own single-line market.
export function getMarketsForGame(gameType, gameObj) {
  const sub = gameObj?.sub || [];
  if (!sub.length) return [];

  const groups = {};
  const singles = [];
  sub.forEach((s) => {
    if (!s.nat) return;
    const entry = { sid: s.sid, nat: s.nat };
    if (String(s.etype || "").toLowerCase().startsWith("fancy")) {
      singles.push([entry]);
    } else {
      const key = s.subtype || "main";
      (groups[key] = groups[key] || []).push(entry);
    }
  });
  return [...Object.values(groups), ...singles];
}

const betNat = (b) => b.selection || b.player_name || b.nation || b.nat || "";
const betOdds = (b) => parseFloat(b.odds ?? b.rate) || 0;
const betStake = (b) => parseFloat(b.stake ?? b.amount) || 0;
const betIsLay = (b) => String(b.type || b.betType || "").toLowerCase() === "lay";

// Per-game book — exactly tenexch useCasinoMyBets' computed branch.
export function computeCasinoBook(gameType, bets = [], gameObj = null) {
  const myBets = Array.isArray(bets) ? bets : [];

  // teen (Teenpatti One Day): three markets share one set of runner names — the
  // feed calls sid 1 AND sid 17 "Player A" (Main, etype "match" / Consecutive,
  // etype "fancy1"). computeUserBook only keys a book under a plain name when
  // that name is unique, so for this game it produced `sid:*` keys and nothing
  // else: the table looked up "Player A" and found nothing, and no book showed
  // under Main, Consecutive or the Card cells at all.
  //
  // The stored bet row does say which market it came from, by exactly the rule
  // the backend settles on (CasinoService.isTeenOneDayConsecutiveBet): a bet
  // whose selection is the bare runner name and whose mtype is NOT "match" is a
  // Consecutive bet. Book the three markets under the keys BetTableTeen reads:
  // "Player A" (Main), "Player A Consecutive", and "Card 3 Odd" for the cells.
  if (gameType === "teen") {
    const fromFeed = (gameObj?.sub || [])
      .filter((s) => String(s.subtype || "").toLowerCase() === "teen" && s.nat)
      .map((s) => String(s.nat).trim());
    const mainNats = fromFeed.length ? fromFeed : ["Player A", "Player B"];
    const book = {};
    const add = (k, v) => { if (k) book[k] = (book[k] || 0) + v; };
    const con = {};

    myBets.forEach((b) => {
      const nat = String(betNat(b)).trim();
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!nat || !odds || !stake) return;
      const isLay = betIsLay(b);
      const isMainRunner = mainNats.includes(nat);
      const isMain = String(b.mtype || b.etype || "").toLowerCase() === "match";

      // Main is the two-way A-vs-B book: the backed runner holds the profit,
      // the other one the stake.
      if (isMainRunner && isMain) {
        mainNats.forEach((n) => add(n, n === nat
          ? (isLay ? -1 : 1) * (odds - 1) * stake
          : (isLay ? 1 : -1) * stake));
        return;
      }
      // Consecutive: one YES/NO line PER PLAYER — back + lay on that player net
      // to min(hit, miss), A and B separate (server game_type teen:conA / conB).
      // Shown under "<Player> Consecutive" so it cannot bleed into Main.
      if (isMainRunner) {
        const s = con[nat] || (con[nat] = { hit: 0, miss: 0 });
        if (isLay) { s.hit -= (odds - 1) * stake; s.miss += stake; } else { s.hit += (odds - 1) * stake; s.miss -= stake; }
        return;
      }
      // Card N Odd/Even cells are single lines (back only).
      add(nat, isLay ? -(odds - 1) * stake : -stake);
    });
    Object.entries(con).forEach(([nat, { hit, miss }]) => { book[`${nat} Consecutive`] = Math.round(Math.min(hit, miss) * 100) / 100; });
    return book;
  }

  // cmeter1: multiplier game (max 12×) — worst case −12×stake on the fighter.
  if (gameType === "cmeter1") {
    const book = {};
    myBets.forEach((b) => {
      const nat = betNat(b);
      const stake = betStake(b);
      if (!nat || !stake) return;
      book[nat] = (book[nat] || 0) - 12 * stake;
    });
    return book;
  }
  // dum10: "Next Total …" is two-sided (<nat>back / <nat>lay), the rest are
  // single-line liability.
  if (gameType === "dum10") {
    const book = {};
    const add = (k, v) => { book[k] = (book[k] || 0) + v; };
    myBets.forEach((b) => {
      const nat = betNat(b);
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!nat || !odds || !stake) return;
      const isLay = betIsLay(b);
      if (/^Next Total/i.test(nat)) {
        if (isLay) { add(`${nat}back`, -(odds - 1) * stake); add(`${nat}lay`, stake); }
        else { add(`${nat}back`, (odds - 1) * stake); add(`${nat}lay`, -stake); }
      } else {
        add(nat, isLay ? -(odds - 1) * stake : -stake);
      }
    });
    return book;
  }
  // notenum: every selection ("Even Card 3", "Card 3 A", "Baccarat 1") is its
  // own two-way line, booked by EXACT nat like the server (casinoMarketBook
  // DYNAMIC_MARKETS.notenum): hit = P/L if it lands, miss = if it doesn't, the
  // cell shows min(hit, miss). Back 100 @2 + lay 100 @2.02 → −2 (not −202).
  // Exact nat is also the reference's per-position figure. Trio and Race to 17
  // lines are YES/NO lines booked the same way on the server (DYNAMIC_MARKETS
  // trio / race17, tenexch d814261a / 6015a67f).
  if (gameType === "notenum" || gameType === "trio" || gameType === "race17") {
    const sides = {};
    myBets.forEach((b) => {
      const nat = betNat(b);
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!nat || !odds || !stake) return;
      const win = (odds - 1) * stake;
      const s = sides[nat] || (sides[nat] = { hit: 0, miss: 0 });
      if (betIsLay(b)) { s.hit -= win; s.miss += stake; } else { s.hit += win; s.miss -= stake; }
    });
    const book = {};
    Object.entries(sides).forEach(([nat, { hit, miss }]) => {
      book[nat] = Math.min(hit, miss);
    });
    return book;
  }
  // teen9 (Teenpatti Test) side bets show the potential WIN on the backed cell.
  //
  // teen8 (Teenpatti Open) used to be in here too — that came from tenexch
  // (useCasinoMyBets.js:118). Per user, its cells must read like every other
  // casino table: the LIABILITY the bet carries (back-only, so −stake), not the
  // profit it would make. Back Player 1 at 1.98 for 100 now shows −100, not +98.
  // goal: two markets — "Who Will Goal Next?" (subtype player) and "Method Of
  // Next Goal" (subtype goal). Both carry a runner named "No Goal": a bet carries
  // the row it was clicked in as mtype ("player" / "method"), so it books in that
  // market only, as the server does; an older bet without it books in EACH. Its
  // "No Goal" row shows the profit and every other runner of that market the
  // stake it loses. Keyed by `sid:<sid>` (GoalMarkets reads that first — the
  // plain name is not unique).
  if (gameType === "goal") {
    const groups = {};
    (gameObj?.sub || []).forEach((s) => {
      if (!s.nat) return;
      const key = String(s.subtype || "main").toLowerCase();
      (groups[key] = groups[key] || []).push({ sid: s.sid, nat: String(s.nat).trim() });
    });
    const book = {};
    const marketOf = (b) => ({ player: "player", method: "goal" })[String(b.mtype || "").toLowerCase()];
    Object.entries(groups).forEach(([key, market]) => {
      const inMarket = myBets.filter((b) => market.some((x) => x.nat === String(betNat(b)).trim())
        && (!marketOf(b) || marketOf(b) === key));
      Object.entries(computeUserBook(inMarket, [market])).forEach(([k, v]) => {
        if (k.startsWith("sid:")) book[k] = v;
      });
    });
    return book;
  }

  const singleLineWin = gameType === "teen9";

  let markets;
  if (["poker20", "abj", "3cardj", "worli", "worli2", "ab3"].includes(gameType)) {
    // Bets stored under a side-qualified nat the feed sub[] doesn't carry —
    // one single-line market per distinct bet selection.
    const seen = new Set();
    markets = [];
    myBets.forEach((b) => {
      const nat = betNat(b);
      if (!nat || seen.has(nat)) return;
      seen.add(nat);
      markets.push([{ sid: `${gameType}:${nat}`, nat }]);
    });
  } else if (gameType === "trap" || gameType === "teen6") {
    // Player A/B is a 2-way match; per-card side markets are single-line.
    const MAIN = new Set(["Player A", "Player B"]);
    markets = [[{ sid: 1, nat: "Player A" }, { sid: 2, nat: "Player B" }]];
    const seen = new Set();
    myBets.forEach((b) => {
      const nat = betNat(b);
      if (!nat || MAIN.has(nat) || seen.has(nat)) return;
      seen.add(nat);
      markets.push([{ sid: `${gameType}:${nat}`, nat }]);
    });
  } else {
    markets = getMarketsForGame(gameType, gameObj);
  }
  // 0 entries are kept (tenexch tables print a 0 book).
  const book = computeUserBook(myBets, markets, { singleLineWin });
  // Two-way YES/NO lines taking back AND lay (server DYNAMIC_MARKETS): teensin
  // "Lucky 9", btable "Odd" — back + lay nets to min(hit, miss) (tenexch 551a4da7).
  const twoWayLine = { teensin: ["Lucky 9", /^lucky\s*9$/i], btable: ["Odd", /^odd$/i] }[gameType];
  if (twoWayLine) {
    const [name, re] = twoWayLine;
    const s = { hit: 0, miss: 0, any: false };
    myBets.forEach((b) => {
      if (!re.test(String(betNat(b)).trim())) return;
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!odds || !stake) return;
      s.any = true;
      if (betIsLay(b)) { s.hit -= (odds - 1) * stake; s.miss += stake; } else { s.hit += (odds - 1) * stake; s.miss -= stake; }
    });
    if (s.any) book[name] = Math.min(s.hit, s.miss);
  }
  // trap "Card N JQK" is two-way per card on the server (DYNAMIC_MARKETS.trap):
  // back + lay on the same card nets to min(hit, miss).
  if (gameType === "trap") {
    const lines = {};
    myBets.forEach((b) => {
      const nat = String(betNat(b)).trim();
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!/^card\s+\d+\s+jqk$/i.test(nat) || !odds || !stake) return;
      const l = lines[nat] || (lines[nat] = { hit: 0, miss: 0 });
      if (betIsLay(b)) { l.hit -= (odds - 1) * stake; l.miss += stake; } else { l.hit += (odds - 1) * stake; l.miss -= stake; }
    });
    Object.entries(lines).forEach(([nat, { hit, miss }]) => { book[nat] = Math.min(hit, miss); });
  }
  // card32eu colour rows (Any Three Card Black / Red, Two Black Two Red) take back
  // AND lay and are two-way per row on the server (DYNAMIC_MARKETS.card32eu).
  if (gameType === "card32eu") {
    const sides = {};
    myBets.forEach((b) => {
      const nat = String(betNat(b)).trim();
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!/^(any three card (black|red)|two black two red)$/i.test(nat) || !odds || !stake) return;
      const win = (odds - 1) * stake;
      const s = sides[nat] || (sides[nat] = { hit: 0, miss: 0 });
      if (betIsLay(b)) { s.hit -= win; s.miss += stake; } else { s.hit += win; s.miss -= stake; }
    });
    Object.entries(sides).forEach(([nat, { hit, miss }]) => { book[nat] = Math.min(hit, miss); });
  }
  // patti2 "Total A" / "Total B" bets carry their line ("Total A 15" back = Yes at
  // total >= 15; "Total A 14" lay = No, wins only BELOW 14 — helper/patti2Settle.js).
  // The cell shows the worst P/L over every total 0–60, so back + lay on one side
  // nets (tenexch worstLineBook); the server header keeps the summed rows.
  if (gameType === "patti2") {
    ["Total A", "Total B"].forEach((name) => {
      const legs = [];
      myBets.forEach((b) => {
        const m = String(betNat(b)).trim().match(PATTI2_TOTAL);
        const odds = betOdds(b);
        const stake = betStake(b);
        if (!m || `Total ${m[1].toUpperCase()}` !== name || !odds || !stake) return;
        legs.push({ line: Number(m[2]), lay: betIsLay(b), win: (odds - 1) * stake, stake });
      });
      if (legs.length) book[name] = worstLineBook(legs, 0, 60);
    });
  }
  // mogambo "3 Card Total" bets carry their line; all settle on one 3-card total
  // (one deck: 4–38) — the server books the same ladder (DYNAMIC_MARKETS.mogambo).
  if (gameType === "mogambo") {
    const legs = [];
    myBets.forEach((b) => {
      const m = String(betNat(b)).trim().match(MOGAMBO_TOTAL);
      const odds = betOdds(b);
      const stake = betStake(b);
      if (m && odds && stake) legs.push({ line: Number(m[1]), lay: betIsLay(b), win: (odds - 1) * stake, stake });
    });
    if (legs.length) book["3 Card Total"] = worstLineBook(legs, 4, 38);
  }
  // Super Over / Super Over 2 / Mini (server DYNAMIC_MARKETS.superover*): a Fancy
  // session row shows its ladder's worst total; a Fancy1 / Common line (and Mini's
  // Tie) is two-way — back + lay on it nets to min(hit, miss), not the sum.
  if (gameType === "superover" || gameType === "superover2" || gameType === "superover3") {
    const byNat = {};
    Object.entries(superOverSessionLadders(myBets)).forEach(([nat, { pl }]) => { byNat[nat] = Math.min(...pl); });
    const lines = {};
    myBets.forEach((b) => {
      const sel = String(betNat(b)).trim();
      const odds = betOdds(b);
      const stake = betStake(b);
      const twoWay = /\bover\b|^common\s/i.test(sel) || (gameType === "superover3" && /^tie$/i.test(sel));
      if (!twoWay || SUPEROVER_SESSION.test(sel) || !odds || !stake) return;
      const l = lines[sel] || (lines[sel] = { hit: 0, miss: 0 });
      if (betIsLay(b)) { l.hit -= (odds - 1) * stake; l.miss += stake; } else { l.hit += (odds - 1) * stake; l.miss -= stake; }
    });
    Object.entries(lines).forEach(([sel, { hit, miss }]) => { byNat[sel] = Math.min(hit, miss); });
    // Feed nats can carry stray spaces (" 0.4 Over Zero RSA").
    markets.forEach((sels) => sels.forEach(({ nat }) => {
      const v = byNat[String(nat || "").trim()];
      if (v !== undefined) book[nat] = v;
    }));
  }
  // 5Five Cricket Fancy ("Aus Over 3 45") is two-way per exact line on the server
  // (DYNAMIC_MARKETS.cricketv3): back + lay on one line nets to min(hit, miss); the row
  // shows the sum of its lines' worst cases under the bare feed nat ("Aus Over 3").
  if (gameType === "cricketv3") {
    const lines = {};
    myBets.forEach((b) => {
      const sel = String(betNat(b)).trim();
      const m = sel.match(/^([A-Za-z]+\s+over\s+\d+)\s+\d+(?:\.\d+)?$/i);
      const odds = betOdds(b);
      const stake = betStake(b);
      if (!m || !odds || !stake) return;
      const win = (odds - 1) * stake;
      const l = lines[sel] || (lines[sel] = { nat: m[1].toLowerCase(), hit: 0, miss: 0 });
      if (betIsLay(b)) { l.hit -= win; l.miss += stake; } else { l.hit += win; l.miss -= stake; }
    });
    const byNat = {};
    Object.values(lines).forEach((l) => { byNat[l.nat] = (byNat[l.nat] || 0) + Math.min(l.hit, l.miss); });
    markets.forEach((sels) => sels.forEach(({ nat }) => {
      const v = byNat[String(nat || "").trim().toLowerCase()];
      if (v !== undefined) book[nat] = v;
    }));
  }
  return book;
}

// Worst P/L over every total of line bets that all settle on one total (back wins
// at total >= line, lay below it) — tenexch worstLineBook.
function worstLineBook(legs, minTotal, maxTotal) {
  let worst = Infinity;
  for (let t = minTotal; t <= maxTotal; t += 1) {
    worst = Math.min(worst, legs.reduce((sum, l) => sum + (l.lay
      ? (t < l.line ? l.stake : -l.win)
      : (t >= l.line ? l.win : -l.stake)), 0));
  }
  return worst;
}

// Legacy get-exposure rows → map (tenexch useCasinoMyBets legacy branch):
// exact name, lowercase name, and the trailing-number-stripped base name
// ("3 Card Total 19" → "3 Card Total", summed).
export function buildLegacyExposureMap(rows = []) {
  const expMap = {};
  (rows || []).forEach((item) => {
    const name = item.team_name || "";
    const exp = parseFloat(item.exposure_amount) || 0;
    if (name && exp !== 0) {
      expMap[name] = exp;
      expMap[name.toLowerCase()] = exp;
      const baseMatch = name.match(/^(.+?)\s+\d+$/);
      if (baseMatch) {
        expMap[baseMatch[1]] = (expMap[baseMatch[1]] || 0) + exp;
      }
    }
  });
  return expMap;
}

// d99 tables look up exposures[nat] ?? exposures[nat.toLowerCase()] (some also
// trimmed variants). Round away float noise ((1.97-1)*100 = 97.00000000000001)
// and add the lowercase alias without overriding an exact key.
export function withLookupAliases(book = {}) {
  const out = {};
  Object.entries(book).forEach(([k, v]) => {
    out[k] = Math.round((Number(v) || 0) * 100) / 100;
  });
  Object.keys(out).forEach((k) => {
    const lower = k.toLowerCase();
    if (out[lower] === undefined) out[lower] = out[k];
    const trimmed = k.trim();
    if (out[trimmed] === undefined) out[trimmed] = out[k];
  });
  return out;
}

// Profit / liability as the reference prints it: no "+" on a profit, a "-" on a liability, up to
// 2 decimals only when the value has them, no commas (user 2026-10-23) — 15 → "15", -14.5 → "-14.5", 1345 → "1345".
export function formatBook(val) {
  let n = Math.round((Number(val) || 0) * 100) / 100;
  if (Object.is(n, -0)) n = 0;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2, useGrouping: false });
}

// Instant Teenpatti 1 / 3.0 main market reads "Player A-Main"; 2.0 (teen32)
// keeps the plain "Player A", like the reference.
const MAIN_NATION_GAMES = new Set(["teen3", "teen33"]);

// 2 Cards Teenpatti Total bet as stored: side + LINE ("Total A 14").
const PATTI2_TOTAL = /^Total ([AB]) (\d+)$/i;
// Mogambo 3 Card Total bet as stored: "3 Card Total 20" at rate/100 + 1.
const MOGAMBO_TOTAL = /^3 Card Total (\d+)$/i;
// Race 20 Total bet as stored: market + LINE ("Total cards 13").
const RACE20_TOTAL = /^Total (points|cards) (\d+)$/i;
// Trio Session is quoted on a fixed line.
const TRIO_SESSION_LINE = 21;
// Super Over Fancy session bet as stored: row + run LINE ("1 over run bhav ENG 13",
// priced at rate/100 + 1); an innings makes at most 36 runs (six sixes).
const SUPEROVER_SESSION = /^(\d+\s+over\s+run(?:\s+bhav)?\s+\S+)\s+(\d+(?:\.\d+)?)$/i;
const SUPEROVER_MAX_RUNS = 36;

// Each Fancy row's P/L at every innings total 0–36 — all its bets settle on that
// team's total, whatever line they took (tenexch superOverSessionLadders).
// Returns { [row]: { pl: number[37], lines: number[] } }.
function superOverSessionLadders(bets = []) {
  const out = {};
  bets.forEach((b) => {
    const m = String(betNat(b)).trim().match(SUPEROVER_SESSION);
    const odds = betOdds(b);
    const stake = betStake(b);
    if (!m || !odds || !stake) return;
    const win = (odds - 1) * stake;
    const line = Number(m[2]);
    const row = out[m[1]] || (out[m[1]] = { pl: new Array(SUPEROVER_MAX_RUNS + 1).fill(0), lines: [] });
    if (!row.lines.includes(line)) row.lines.push(line);
    for (let r = 0; r <= SUPEROVER_MAX_RUNS; r++) {
      row.pl[r] += betIsLay(b) ? (r >= line ? -win : stake) : (r >= line ? win : -stake);
    }
  });
  return out;
}

// Super Over "Run Amount" per Fancy row: the P/L just below and at each bet line
// (a No 13 @ 84 reads 12: +100, 13: -84) — the same figures as the row's book.
export function superOverRunLadders(bets = []) {
  const out = {};
  Object.entries(superOverSessionLadders(bets)).forEach(([nat, { pl, lines }]) => {
    const points = [...new Set(lines.flatMap((l) => [Math.ceil(l) - 1, Math.ceil(l)]))]
      .filter((p) => p >= 0 && p <= SUPEROVER_MAX_RUNS)
      .sort((x, y) => x - y);
    out[nat] = points.map((p) => ({ score: `${p}`, position: Math.round(pl[p] * 100) / 100 }));
  });
  return out;
}

// Super Over My Bets: Bookmaker, then Fancy (session rows), then Fancy1 (ball /
// Common; Mini's Tie too), placement order inside each; a session bet reads
// "<row> / <rate>" with its run line as the rate, like the reference (tenexch).
export function superOverMyBets(gameType, bets = []) {
  const groupOf = (b) => {
    const sel = String(betNat(b)).trim();
    if (SUPEROVER_SESSION.test(sel)) return 1;
    return /^(0\.\d\s+over|common)\s/i.test(sel) || (gameType === "superover3" && /^tie$/i.test(sel)) ? 2 : 0;
  };
  return [0, 1, 2].flatMap((g) => bets.filter((b) => groupOf(b) === g)).map((b) => {
    const m = String(betNat(b)).trim().match(SUPEROVER_SESSION);
    return m ? { ...b, player_name: `${m[1]} / ${Math.round((betOdds(b) - 1) * 100)}`, odds: m[2] } : b;
  });
}

// Teenpatti Joker 20-20 market names in the reference's own spelling.
const JOKER20_MARKETS = {
  "player a": "Player", "player b": "Player",
  "joker even": "oddeven", "joker odd": "oddeven",
  "joker red": "redblack", "joker black": "redblack",
  "joker spade": "card", "joker heart": "card", "joker diamond": "card", "joker club": "card",
};

// Unlimited Joker pick 1..13 → card label (1 = A, 11 = J, 12 = Q, 13 = K).
const jokerLabel = (n) => ({ 1: "A", 11: "J", 12: "Q", 13: "K" }[n] || String(n));

// Roulettes: the bet type after the name, as the reference — "10 - Single",
// "1,2 - Split", "1st 12 - Dozen", "Red - Half"; VIP / Beach / Roulette also
// "3rd Column - Column". Unique Roulette bets carry their type in player_name
// ("5,6 - Street", by board position) — that wins. Display only.
const ROULETTE_GAMES = new Set(["ourroullete", "roulette11", "roulette12", "roulette13"]);
const ROULETTE_INSIDE_TYPES = { 1: "Single", 2: "Split", 3: "Street", 4: "Corner" };
const ROULETTE_OUTSIDE_TYPES = {
  "1st 12": "Dozen", "2nd 12": "Dozen", "3rd 12": "Dozen",
  "1 to 18": "Half", "19 to 36": "Half", red: "Half", black: "Half", odd: "Half", even: "Half",
};
function formatRouletteNation(gameType, nat, bet) {
  const stored = String(bet?.player_name ?? "").trim();
  if (gameType === "ourroullete" && /^\d+(\s*,\s*\d+)* - (Single|Split|Street|Corner)$/.test(stored)) return stored;
  const s = String(nat ?? "").trim();
  if (/^\d+(\s*,\s*\d+)*$/.test(s)) {
    const type = ROULETTE_INSIDE_TYPES[s.split(",").length];
    return type ? `${s} - ${type}` : nat;
  }
  if (gameType !== "ourroullete" && /^(1st|2nd|3rd) column$/i.test(s)) return `${s} - Column`;
  const outside = ROULETTE_OUTSIDE_TYPES[s] || ROULETTE_OUTSIDE_TYPES[s.toLowerCase()];
  return outside ? `${s} - ${outside}` : nat;
}

// Goal: rdesc "<player>#<method>" reads "<method> by <player>"; a no-goal round
// ("No Goal#No Goal") reads "No Goal" (tenexch formatGoalResultText).
export function formatGoalResultText(rdesc) {
  const [player, method] = String(rdesc || "").split("#").map((s) => s.trim());
  if (!method) return player || "";
  if (/^no goal$/i.test(method) || /^no goal$/i.test(player)) return "No Goal";
  return player ? `${method} by ${player}` : method;
}

// ---- Worli / Instant Worli / Matka (tenexch casinoUtils) ----
export const SP_PANA = {
  1: [128, 137, 146, 236, 245, 290, 380, 470, 489, 560, 579, 678],
  2: [129, 138, 147, 156, 237, 246, 345, 390, 480, 570, 589, 679],
  3: [120, 139, 148, 157, 238, 247, 256, 346, 490, 580, 670, 689],
  4: [130, 149, 158, 167, 239, 248, 257, 347, 356, 590, 680, 789],
  5: [140, 159, 168, 230, 249, 258, 267, 348, 357, 456, 690, 780],
  6: [123, 150, 169, 178, 240, 259, 268, 349, 358, 367, 457, 790],
  7: [124, 160, 179, 250, 269, 278, 340, 359, 368, 458, 467, 890],
  8: [125, 134, 170, 189, 260, 279, 350, 369, 378, 459, 468, 567],
  9: [126, 135, 180, 234, 270, 289, 360, 379, 450, 469, 478, 568],
  0: [127, 136, 145, 190, 235, 280, 370, 389, 460, 479, 569, 578],
};

export const DP_PANA = {
  1: [100, 119, 155, 227, 335, 344, 399, 588, 669],
  2: [110, 200, 228, 255, 336, 499, 660, 688, 778],
  3: [166, 229, 300, 337, 355, 445, 599, 779, 788],
  4: [112, 220, 266, 338, 400, 446, 455, 699, 770],
  5: [113, 122, 177, 339, 366, 447, 500, 799, 889],
  6: [114, 277, 330, 448, 466, 556, 600, 880, 899],
  7: [115, 133, 188, 223, 377, 449, 557, 566, 700],
  8: [116, 224, 233, 288, 440, 477, 558, 800, 990],
  9: [117, 144, 199, 225, 388, 559, 577, 667, 900],
  0: [118, 226, 244, 299, 334, 488, 550, 668, 677],
};

export const TP_PANA = [777, 444, 111, 888, 555, 222, 999, 666, 333, "000"];

// Worli / Instant Worli bet names the reference way (bet slip, My Bets, result
// modals). The boards store a hyphenated key — "single-3", "single-line1",
// "pana-590", "sp-pana-590", "sp-all", "trio-all" — and settlement matches on
// that key, so this is display only: "single-3" → "3 Single",
// "single-line1" → "Line 1 Single", "single-odd" → "Odd Single",
// "pana-590" → "590 Pana", "sp-pana-590" → "590 SP", "sp-all" → "All SP".
const WORLI_SUFFIX = { line1: "Line 1", line2: "Line 2", odd: "Odd", even: "Even", all: "ALL", cut: "Cut" };
const WORLI_LABELS = {
  single: "Single", pana: "Pana", sp: "SP", dp: "DP", tp: "TP", trio: "Trio",
  cycle: "Cycle", motorsp: "Motor SP", c56: "56", c64: "64", abr: "ABR",
  commonsp: "Common SP", commondp: "Common DP", colordp: "color DP", // reference result modal: "1 color DP" (slip keeps "Color DP - 1")
};
// Matka (worli3) names the reference way (admin View More, user 2026-10-17):
// Jodi "0-2" → "2 Jodi" (the number, no leading zero), an exact pana
// "sp-pana-157" / "dp-pana-112" / "tp-pana-111" → "157 Pana"; everything else
// as Worli ("1 SP", "Line 1 Single", "1 color DP"). Display only.
function formatWorli3Nation(nat) {
  const s = String(nat || "").trim();
  const jodi = s.match(/^(\d)-(\d)$/);
  if (jodi) return `${Number(jodi[1] + jodi[2])} Jodi`;
  const pana = s.match(/^(?:sp|dp|tp)-pana-(\d{3})$/i);
  if (pana) return `${pana[1]} Pana`;
  return formatWorliNation(s);
}

function formatWorliNation(nat) {
  const m = String(nat || "").trim().match(/^([a-z0-9]+)-(.+)$/i);
  if (!m) return nat;
  const label = WORLI_LABELS[m[1].toLowerCase()];
  if (!label) return nat;
  const raw = m[2].replace(/^pana-/i, "");
  // Motor SP digits print in Matka order — ascending, 0 last ("0137" → "1370"), like the strip (user).
  if (label === "Motor SP" && /^\d+$/.test(raw)) return `${formatMatkaPana(raw.split(""))} ${label}`;
  const val = WORLI_SUFFIX[raw.toLowerCase()] || raw.charAt(0).toUpperCase() + raw.slice(1);
  // 56 / 64 charts: My Bets / result modals read "1 - Chart 56" on the reference
  // (the bet slip reads "56 - 1" — see formatWorliSlipName).
  // ALL cells print the word in caps everywhere (user): "ALL SP", "ALL - Chart 56".
  // 56 / 64 charts" ALL cell drops the dash (user): "ALL Chart 56" / "ALL Chart 64".
  if ((label === "56" || label === "64") && raw.toLowerCase() === "all") return `${val} Chart ${label}`;
  if (label === "56" || label === "64") return `${val} - Chart ${label}`;
  // ABR (reference result modal): "A Only ABR" / "B Only ABR" / "R Only ABR", the ABR cell "ABR Only".
  if (label === "ABR") {
    if (raw.toLowerCase() === "all") return "ABR Only";
    if (raw.toLowerCase() === "cut") return "ABR Cut Only";
    if (/^(a|b|r|ab|ar|br)$/i.test(raw)) return `${raw.toUpperCase()} Only ABR`; // combos too: "AB Only ABR"
    return `${raw.toUpperCase()} ABR`;
  }
  return `${val} ${label}`;
}
const WORLI_SLIP_ALL_LABELS = { sp: "SP", dp: "DP", c56: "56", c64: "64" };
// Bet-slip name: same as formatWorliNation except the 56 / 64 charts, which the
// reference's slip prints label-first ("56 - 1", "64 - All").
export function formatWorliSlipName(nat) {
  // "ALL" cells print label-first with ALL in caps on the slip (user): "SP - ALL",
  // "DP - ALL". Exceptions (user): 56 / 64 charts "56CHART" / "64CHART", Color DP
  // "Color DP - 11", Trio "ALL Trio"; ABR's keeps the reference's "ABR - ABR". My Bets / result modals unchanged.
  if (/^colordp-all$/i.test(String(nat || "").trim())) return "Color DP - 11"; // user: Color DP's ALL cell slip
  const chartAll = String(nat || "").trim().match(/^c(56|64)-all$/i);
  if (chartAll) return `${chartAll[1]}CHART`; // user: 56 / 64 chart's ALL cell slip → "56CHART" / "64CHART"
  const all = String(nat || "").trim().match(/^(sp|dp|c56|c64)-all$/i);
  if (all) return `${WORLI_SLIP_ALL_LABELS[all[1].toLowerCase()]} - ALL`;
  const cm = String(nat || "").trim().match(/^common(sp|dp)-(\d)$/i);
  if (cm) return `Common ${cm[1].toUpperCase()} - ${cm[2]}`; // reference slip: "Common SP - 1"
  const col = String(nat || "").trim().match(/^colordp-(.+)$/i);
  if (col) return `Color DP - ${WORLI_SUFFIX[col[1].toLowerCase()] || col[1]}`; // reference slip: "Color DP - 1"
  const ab = String(nat || "").trim().match(/^abr-(.+)$/i);
  if (ab) return `ABR - ${ab[1].toLowerCase() === "all" ? "ABR" : ab[1].toLowerCase() === "cut" ? "ABR Cut" : ab[1].toUpperCase()}`; // reference slip: "ABR - A", "ABR - ABR", "ABR - ABR Cut"
  const m = String(nat || "").trim().match(/^c(56|64)-(.+)$/i);
  if (!m) return formatWorliNation(nat);
  const raw = m[2];
  return `${m[1]} - ${raw.toLowerCase() === "all" ? "all" : raw}`; // "56 - all" on the reference
}

// Worli Matka / Instant Worli Line 1 / Line 2 / Odd / Even: the reference places ONE bet per
// number in the group (5 numbers) at the board rate 9 — stake 100 → 500 placed,
// a hit pays 800 on one number and loses 400 on the other four (net +400), a
// miss loses all 500. We store that as one bet: stake × 5 at 9 ÷ 5 = 1.8, so the
// generic book (stake) and settlement (stake × odds − stake) land on the same
// 500 / +400 / −500 with no server change. The rate cell shows the board's 9.
export const WORLI_LINE_UNITS = 5;
export const isWorliLineSelection = (nat) => /^single-(line1|line2|odd|even)$/i.test(String(nat || "").trim());
function formatWorliOdds(bet, fallback) {
  const name = bet?.selection || bet?.player_name || bet?.nation || bet?.nat;
  const odds = Number(bet?.odds ?? bet?.rate);
  if (!isWorliLineSelection(name) || !(odds > 0)) return fallback;
  return String(Math.round(odds * WORLI_LINE_UNITS * 100) / 100);
}

// Worli Matka: the panas a board cell covers, by the same rules the server
// settles with (resolveWorli). All 220 panas = 3-digit multisets over 0-9.
// Single-tab cells cover no pana (they bet the ank) → []. Used by the phone
// slip's "Total Pana (N)" box (reference). Cycle / Motor SP keys carry the
// picked digit set ("cycle-1,2", "motorsp-1,2,3,4"); ABR → [].
// Matka writes a pana ascending with 0 last: 4-2-5 → "245", 6-0-4 → "460",
// 0-0-5 → "500". Used for the Pana tab's picked digits and the slip's pana list.
export function formatMatkaPana(digits) {
  const d = (digits || []).map(Number).sort((a, b) => a - b);
  return d.filter((x) => x !== 0).join("") + "0".repeat(d.filter((x) => x === 0).length);
}

const ALL_PANAS = (() => {
  const out = [];
  for (let a = 0; a <= 9; a++) for (let b = a; b <= 9; b++) for (let c = b; c <= 9; c++) out.push([a, b, c]);
  return out;
})();
const RON = new Set(["012", "123", "234", "345", "456", "567", "678", "789", "089", "019"]);
function panaMatches(d, sel) {
  const counts = {}; d.forEach((x) => { counts[x] = (counts[x] || 0) + 1; });
  const uniq = Object.keys(counts).length;
  const isTP = uniq === 1, isDP = uniq === 2, isSP = uniq === 3;
  const single = d.reduce((s, x) => s + x, 0) % 10;
  const has = (n) => d.includes(n);
  const allOdd = d.every((x) => x % 2 === 1), allEven = d.every((x) => x % 2 === 0);
  const in56 = isSP && d.every((x) => x >= 2 && x <= 9);
  const in64 = isSP && d.some((x) => x === 0 || x === 1);
  let m;
  if ((m = sel.match(/^pana-(\d{3})$/))) return m[1].split("").map(Number).sort((a, b) => a - b).join("") === d.join("");
  if ((m = sel.match(/^sp-pana-(\d{3})$/))) return m[1].split("").map(Number).sort((a, b) => a - b).join("") === d.join("");
  if ((m = sel.match(/^sp-(\d)$/))) return isSP && single === Number(m[1]);
  if (sel === "sp-all") return isSP;
  if ((m = sel.match(/^dp-(\d)$/))) return isDP && single === Number(m[1]);
  if (sel === "dp-all") return isDP;
  if (sel === "trio-all") return isTP;
  if ((m = sel.match(/^c56-(\d)$/))) return in56 && single === Number(m[1]);
  if (sel === "c56-all") return in56;
  if ((m = sel.match(/^c64-(\d)$/))) return in64 && single === Number(m[1]);
  if (sel === "c64-all") return in64;
  if ((m = sel.match(/^commonsp-(\d)$/))) return isSP && has(Number(m[1]));
  if ((m = sel.match(/^commondp-(\d)$/))) return isDP && has(Number(m[1]));
  if ((m = sel.match(/^colordp-(\d)$/))) return isDP && (allOdd || allEven) && single === Number(m[1]);
  if (sel === "colordp-all") return isDP && (allOdd || allEven);
  // ABR: A = odd-digit singles (10), B = even-digit singles (10), R = the 10 sequences.
  if (sel === "abr-a") return isSP && allOdd;
  if (sel === "abr-b") return isSP && allEven;
  if (sel === "abr-r") return RON.has(d.join(""));
  if (sel === "abr-ab") return isSP && (allOdd || allEven);
  if (sel === "abr-ar") return (isSP && allOdd) || RON.has(d.join(""));
  if (sel === "abr-br") return (isSP && allEven) || RON.has(d.join(""));
  if (sel === "abr-all") return (isSP && (allOdd || allEven)) || RON.has(d.join(""));
  if (sel === "abr-cut") return isSP && !allOdd && !allEven && !RON.has(d.join("")); // the other 90 singles
  if ((m = sel.match(/^cycle-(.+)$/))) {
    // Two picks, repeats allowed ("23": both digits in the pana; "11": 1 twice).
    const ds = (m[1].match(/\d/g) || []).map(Number);
    if (ds.length !== 2) return false;
    return ds[0] === ds[1] ? d.filter((x) => x === ds[0]).length >= 2 : ds.every((x) => has(x));
  }
  if ((m = sel.match(/^motorsp-(.+)$/))) {
    const ds = new Set((m[1].match(/\d/g) || []).map(Number));
    return ds.size >= 4 && ds.size <= 9 && isSP && d.every((x) => ds.has(x));
  }
  return false;
}
export function worliPanasFor(nat) {
  const sel = String(nat || "").trim().toLowerCase();
  if (!sel || sel.startsWith("single-")) return [];
  // Listed in numeric order, as the reference slip does (128, 137, 146, 236 …).
  return ALL_PANAS.filter((d) => panaMatches(d, sel))
    .map(formatMatkaPana)
    .sort((a, b) => Number(a) - Number(b));
}

// Bet name as My Bets and the result modals print it. Note Number card-value
// bets are stored "Card 2 A" (the shape settlement parses) and read
// "Card 2 - Card A", like the reference. Teenpatti 1-day stores "Player A" for
// Main (mtype match) and Consecutive, and "Card 2 Odd"; they read
// "Player A-Main" / "Player A-CON" / "Card 2 - Odd". Teenpatti 2.0 (tenexch
// formatTeen6Nation) reads "Player A-Main", "Player A Under 21-UO",
// "Card 5 - Diamond", "Card 1 - Odd" and "Card 1 - Card A". Unique Teenpatti,
// 2 Cards Teenpatti and the Joker tables follow tenexch's helpers (below). Display only.
export function formatBetNation(gameType, nat, bet) {
  if (ROULETTE_GAMES.has(gameType)) return formatRouletteNation(gameType, nat, bet);
  if (gameType === "teen") {
    const n = String(nat || "").trim();
    if (/^Player [AB]$/.test(n)) {
      return `${n}-${String(bet?.mtype || "match").toLowerCase() === "match" ? "Main" : "CON"}`;
    }
    const m = n.match(/^(Card \d+) (Odd|Even)$/i);
    return m ? `${m[1]} - ${m[2]}` : n;
  }
  // Jack Top / Queen Top Open (teen42 / teen41): same markets and names as teen6.
  if (gameType === "teen6" || gameType === "teen42" || gameType === "teen41") {
    const n = String(nat || "").trim();
    if (/^Player [AB]$/i.test(n)) return `${n}-Main`;
    if (/^Player [AB] (Under|Over) \d+$/i.test(n)) return `${n}-UO`;
    const side = n.match(/^Card (\d) (Spade|Heart|Club|Diamond|Odd|Even)$/i);
    if (side) return `Card ${side[1]} - ${side[2]}`;
    const face = n.match(/^Card (\d) (A|J|Q|K|\d{1,2})$/i);
    return face ? `Card ${face[1]} - Card ${face[2].toUpperCase()}` : n;
  }
  // Andar Bahar 2: "1st Bet SB" / "2nd Bet SA" read "1st Bet B" / "2nd Bet A".
  if (gameType === "abj") return String(nat || "").replace(/^((?:1st|2nd) Bet) S([AB])$/i, "$1 $2");
  // 20-20 Poker: Winner is stored "Player A/B" (the feed names both sides
  // "Winner"); it reads "Winner A/B".
  if (gameType === "poker20") {
    const m = String(nat || "").trim().match(/^Player ([AB])$/i);
    return m ? `Winner ${m[1].toUpperCase()}` : nat;
  }
  // Trio Session is stored "Session" at rate/100 + 1 → "Session-80" / "Session-100".
  if (gameType === "trio") {
    const odds = Number(bet?.odds ?? bet?.rate);
    return /^session$/i.test(String(nat || "").trim()) && odds > 1 ? `Session-${Math.round((odds - 1) * 100)}` : nat;
  }
  // Race 20 Totals: stored "Total cards 13" at rate/100 + 1 → "Total cards / 90".
  if (gameType === "race20") {
    const t = String(bet?.selection || nat || "").trim().match(RACE20_TOTAL);
    const odds = Number(bet?.odds ?? bet?.rate);
    return t && odds > 1 ? `Total ${t[1].toLowerCase()} / ${Math.round((odds - 1) * 100)}` : nat;
  }
  // The Trap: "Player A-Main"; side bets "Card 1 JQK" / "Card 3 Low" → "Card 1 - JQK" / "Card 3 - Low".
  if (gameType === "trap") {
    const n = String(nat || "").trim();
    const m = n.match(/^Card\s+(\d+)\s+(Low|High|JQK)$/i);
    if (m) return `Card ${m[1]} - ${m[2].toUpperCase() === "JQK" ? "JQK" : m[2][0].toUpperCase() + m[2].slice(1).toLowerCase()}`;
    return /^Player [AB]$/.test(n) ? `${n}-Main` : n;
  }
  // Unique Teenpatti stores the picked positions ("234"): "Your card 234".
  if (gameType === "teenunique") {
    const n = String(nat || "").trim();
    return /^[1-6]{3}$/.test(n) ? `Your card ${n}` : nat;
  }
  // 2 Cards Teenpatti: "Player A-Main", "Mini Baccarat A-Baccarat", "Color Plus-Plus";
  // a Total bet (priced at the decimal bhav/100 + 1) → "Total A-95".
  if (gameType === "patti2") {
    const sel = String(bet?.selection || nat || "").trim();
    const t = sel.match(PATTI2_TOTAL);
    const odds = Number(bet?.odds ?? bet?.rate);
    if (t && odds > 1) return `Total ${t[1].toUpperCase()}-${Math.round((odds - 1) * 100)}`;
    if (/^Player [AB]$/i.test(sel)) return `${sel}-Main`;
    if (/^Mini Baccarat [AB]$/i.test(sel)) return `${sel}-Baccarat`;
    if (/^Color Plus$/i.test(sel)) return "Color Plus-Plus";
    return nat;
  }
  // Teenpatti Joker 20-20: "<name> - <market>" ("Player B - Player", "Joker Black - redblack").
  if (gameType === "joker20") {
    const market = JOKER20_MARKETS[String(nat || "").trim().toLowerCase()];
    return market ? `${String(nat).trim()} - ${market}` : nat;
  }
  // Unlimited Joker: the joker the bet was placed with — "Player A - Joker J".
  if ((gameType === "joker1" || gameType === "joker120") && Number(bet?.joker)) {
    return `${nat} - Joker ${jokerLabel(Number(bet.joker))}`;
  }
  // Mogambo 3 Card Total: stored "3 Card Total 20" at rate/100 + 1 → "3 Card Total-90".
  if (gameType === "mogambo") {
    const odds = Number(bet?.odds ?? bet?.rate);
    return MOGAMBO_TOTAL.test(String(bet?.selection || nat || "").trim()) && odds > 1 ? `3 Card Total-${Math.round((odds - 1) * 100)}` : nat;
  }
  // Worli / Instant Worli / Matka board keys the reference way ("single-line1" → "Line 1 Single", "sp-all" → "ALL SP", "3-5" → "35 Jodi").
  if (gameType === "worli" || gameType === "worli2") return formatWorliNation(nat);
  if (gameType === "worli3") return formatWorli3Nation(nat);
  // Lottery: stored "Double 9 8" (play + picked digits); reads "Double-98" (tenexch formatLottcardNation).
  if (gameType === "lottcard") {
    const m = String(bet?.selection || nat || "").trim().match(/^(\S+)\s+([\d\s]+)$/);
    return m ? `${m[1]}-${m[2].replace(/\s+/g, "")}` : nat;
  }
  if (!MAIN_NATION_GAMES.has(gameType) && gameType !== "notenum") return nat;
  const n = String(nat || "").trim();
  if (MAIN_NATION_GAMES.has(gameType)) return /^Player [AB]$/.test(n) ? `${n}-Main` : n;
  const m = gameType === "notenum" ? n.match(/^Card\s+(\d+)\s+(A|10|[2-9])$/i) : null;
  return m ? `Card ${m[1]} - Card ${m[2].toUpperCase()}` : n;
}

// Rate shown in My Bets and the result / statement modals, per game (tenexch
// formatBetOdds). `fallback` is the stored odds (or its net price). Display only.
//  - Muflis Top 9: a WON bet shows its multiple + 1 (×3 → 4 … ×30 → 31; the
//    feed quotes 2 and the hand pays by its highest card — helper/muflisSettle.js).
//  - 2 Cards Teenpatti / Race 20 Totals: the bet's line ("Total A 14" → 14).
//  - Trio Session: its fixed line (21).
//  - Andar Bahar 2 1st / 2nd Bet paid 1 : 0.5 → 1.5; Andar Bahar Bahar on the
//    first card paid 25% → 1.25 (open / lost bets keep the stored odds).
// 1 Card Meter: stored at the feed's cell value, not a price. The round's rate is
// the card point difference (A=1 … K=13, max 12): rdesc "Fighter A#12", else the
// cards (same value other suit = 1; identical = tie, 0) — tenexch cmeter1RoundRate.
const cmeter1CardValue = (code) => {
  const rank = String(code || "").trim().toUpperCase().replace(/[SHCD]+$/, "");
  const named = { A: 1, J: 11, Q: 12, K: 13 }[rank];
  if (named) return named;
  const n = parseInt(rank, 10);
  return n >= 2 && n <= 10 ? n : null;
};
function cmeter1RoundRate(t1) {
  const cards = String(t1?.card || "").split(",").map((c) => c.trim().toUpperCase());
  const [a, b] = cards.map(cmeter1CardValue);
  if (a != null && b != null && cards[0] === cards[1]) return 0;
  const fromRdesc = parseInt(String(t1?.rdesc || "").split("#")[1], 10);
  if (fromRdesc > 0) return Math.min(fromRdesc, 12);
  if (a == null || b == null) return null;
  return Math.min(Math.abs(a - b) || 1, 12);
}

// `t1`: the round's result (result / statement modals) — 1 Card Meter reads its rate from it.
export function formatBetOdds(gameType, bet, fallback, t1) {
  const name = String(bet?.selection || bet?.player_name || bet?.nation || bet?.nat || "").trim();
  const stake = Number(bet?.stake ?? bet?.amount);
  const win = Number(bet?.win);
  const paid = (part) => stake > 0 && win > 0 && Math.abs(win - stake * part) < 0.01;
  switch (gameType) {
    case "teenmuf":
      return /^top\s*9\s*[ab]$/i.test(name) && stake > 0 && win > 0 ? Math.round(win / stake) + 1 : fallback;
    case "patti2": {
      const t = name.match(PATTI2_TOTAL);
      return t ? Number(t[2]) : fallback;
    }
    case "race20": {
      const t = name.match(RACE20_TOTAL);
      return t ? Number(t[2]) : fallback;
    }
    case "trio":
      return /^session$/i.test(name) ? TRIO_SESSION_LINE : fallback;
    case "abj":
      return /^(1st|2nd) Bet\b/i.test(name) && paid(0.5) ? 1.5 : fallback;
    case "ab20":
      return /^bahar\b/i.test(name) && paid(0.25) ? 1.25 : fallback;
    case "cmeter1":
      return cmeter1RoundRate(t1) ?? fallback;
    case "mogambo": {
      const t = name.match(MOGAMBO_TOTAL);
      return t ? Number(t[1]) : fallback;
    }
    case "superover2": { // the session bet's run line, like its My Bets (tenexch)
      const t = name.match(SUPEROVER_SESSION);
      return t ? Number(t[2]) : fallback;
    }
    case "worli":
    case "worli2":
    case "worli3":
      return formatWorliOdds(bet, fallback); // Line / Odd / Even stored at rate ÷ 5 → shows 9.5 / 9
    default:
      return fallback;
  }
}
